import React, { useEffect, useMemo } from 'react';
import { Descriptions, Form, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { getRoutes } from '../../services/taxonomies/routes';
import useDictionaries from '../../hooks/useDictionaries';

const MAX_SLOTS = 4;

const parseCsv = (csv) =>
  (csv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const dictionaries = {
  routes: getRoutes,
};

const LinesRestriction = ({ form, title }) => {
  const { t } = useTranslation();

  const [{ routes }] = useDictionaries(dictionaries);

  const originalLineNames = useMemo(
    () => parseCsv(title?.lines),
    [title?.lines],
  );

  const slotCount = useMemo(() => {
    const linesCount = parseCsv(title?.lines).length;
    return Math.min(linesCount, MAX_SLOTS);
  }, [title?.lines]);

  const originalLineIds = useMemo(
    () =>
      originalLineNames.map((name) => {
        const route = (routes.content || []).find(
          (r) => r && String(r.name) === name,
        );
        return route?.id;
      }),
    [originalLineNames, routes.content],
  );

  const availableLines = useMemo(
    () => (routes.content || []).filter((route) => route && !route.deleted),
    [routes.content],
  );

  useEffect(() => {
    if (title?.id && routes.content) {
      form.setFieldsValue({
        titleId: title.id,
        lines: originalLineIds,
      });
    }
  }, [form, title?.id, routes.content, originalLineIds]);

  const renderLineLabel = (route) => route.name || route.code || `#${route.id}`;

  const originalLinesDisplay = useMemo(() => {
    if (!originalLineNames.length) return '-';
    return originalLineNames.join(', ');
  }, [originalLineNames]);

  return (
    <Form form={form} layout="vertical">
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label={t('entity.account.transportTitle.lines')}>
          {originalLinesDisplay}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Paragraph style={{ marginTop: 16 }}>
        {t('actions.modifyLinesTitle')}
      </Typography.Paragraph>

      <Form.Item name="titleId" hidden />

      {Array.from({ length: slotCount }).map((_, slotIndex) => (
        <Form.Item
          // eslint-disable-next-line react/no-array-index-key -- slots are fixed positions, not a reorderable list
          key={`line-slot-${slotIndex}`}
          shouldUpdate={(prev, cur) =>
            JSON.stringify(prev?.lines) !== JSON.stringify(cur?.lines)
          }
        >
          {() => {
            const selectedLines = form.getFieldValue('lines') || [];
            const otherSelectedIds = new Set(
              selectedLines
                .filter((__, idx) => idx !== slotIndex)
                .filter((v) => v != null)
                .map((v) => String(v)),
            );
            const slotOptions = availableLines.filter(
              (route) => !otherSelectedIds.has(String(route.id)),
            );

            return (
              <Form.Item
                name={['lines', slotIndex]}
                label={`${t('entity.account.transportTitle.lines')} ${
                  slotIndex + 1
                }`}
                rules={[
                  {
                    required: true,
                    message: t('errors.requiredField'),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder={t('entity.account.transportTitle.lines')}
                  style={{ width: '100%' }}
                  loading={routes.loading}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {slotOptions.map((route) => (
                    <Select.Option key={route.id} value={route.id}>
                      {renderLineLabel(route)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
      ))}
    </Form>
  );
};

export default LinesRestriction;
